/**
 * Session adapter — the canonical layer between raw onboarding websocket frames
 * and the UI. Everything here is pure and deterministic so the UX guarantees
 * (durable drafts, bounded retries, repeat suppression, honest progress) are
 * unit-testable without a socket.
 *
 * Nothing in this file talks to the AI service; it only interprets and holds
 * client-side session state.
 */
import type { TOnboardingMetaPublic, TOnboardingProgress } from "@/api/onboarding";
import type { TOnboardingSessionHistoryItem } from "@/hooks/useOnboardingSession";

// ---------------------------------------------------------------------------
// Bounds — every effect/resource has a cap so nothing runs away.
// ---------------------------------------------------------------------------

export const AWAIT_HINT_MS = 5_000;
/** Socket closed abnormally — one silent reconnect after this delay. */
export const SILENT_RECONNECT_MS = 2_000;
/** Socket open but no next-step frame — convert the stall into a reconnect. */
export const AWAIT_STALL_MS = 45_000;
export const MAX_SILENT_RECONNECTS = 1;
export const MAX_QUEUED_SENDS = 20;
export const EVENT_LOG_MAX = 200;

const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
const DRAFT_MAX_CHARS = 32_000;
const DRAFT_PREFIX = "onboarding:draft:";

// ---------------------------------------------------------------------------
// Skip — the canonical utterance the AI's SKIP_PATTERNS already recognise.
// ---------------------------------------------------------------------------

export const SKIP_UTTERANCE = "I'd like to skip this question.";

/** Whether an answer text is the skip marker rather than a real answer. */
export const isSkipText = (text?: string | null): boolean => {
  if (!text) return false;
  const normal = text.trim().toLowerCase();
  return normal === SKIP_UTTERANCE.toLowerCase() || /^i'?d like to skip/.test(normal);
};

// ---------------------------------------------------------------------------
// Durable drafts — typed text survives errors, disconnects, reloads.
// sessionStorage scopes drafts to the tab; keys are per company+question.
// ---------------------------------------------------------------------------

type TDraftRecord = { text: string; savedAt: number };

const draftKey = (companyId: string, questionId: string) => `${DRAFT_PREFIX}${companyId}:${questionId}`;

const safeStorage = (): Storage | null => {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
};

export const loadDraft = (companyId: string, questionId: string): string | null => {
  const storage = safeStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(draftKey(companyId, questionId));
    if (!raw) return null;
    const record = JSON.parse(raw) as TDraftRecord;
    if (!record || typeof record.text !== "string") return null;
    if (Date.now() - record.savedAt > DRAFT_TTL_MS) {
      storage.removeItem(draftKey(companyId, questionId));
      return null;
    }
    return record.text.slice(0, DRAFT_MAX_CHARS);
  } catch {
    return null;
  }
};

export const saveDraft = (companyId: string, questionId: string, text: string): void => {
  const storage = safeStorage();
  if (!storage) return;
  try {
    if (!text.trim()) {
      storage.removeItem(draftKey(companyId, questionId));
      return;
    }
    const record: TDraftRecord = { text: text.slice(0, DRAFT_MAX_CHARS), savedAt: Date.now() };
    storage.setItem(draftKey(companyId, questionId), JSON.stringify(record));
  } catch {
    // Quota/serialization failures must never break the composer.
  }
};

export const clearDraft = (companyId: string, questionId: string): void => {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.removeItem(draftKey(companyId, questionId));
  } catch {
    /* ignore */
  }
};

/** Drop every draft for a company (called once onboarding completes). */
export const clearCompanyDrafts = (companyId: string): void => {
  const storage = safeStorage();
  if (!storage) return;
  try {
    const prefix = `${DRAFT_PREFIX}${companyId}:`;
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key?.startsWith(prefix)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    /* ignore */
  }
};

// ---------------------------------------------------------------------------
// Repeat detection — find a previously-answered question similar to the current
// one so the UI can offer a one-tap prefill (never auto-sent).
// ---------------------------------------------------------------------------

const normalizeText = (text: string): string =>
  text.toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();

const tokenize = (text: string): Set<string> => new Set(normalizeText(text).split(" ").filter(Boolean));

/** Jaccard-style token overlap: |intersection| / |union|. */
export const questionSimilarity = (a: string, b: string): number => {
  const ta = tokenize(a);
  const tb = tokenize(b);
  if (ta.size === 0 || tb.size === 0) return 0;
  let intersection = 0;
  ta.forEach((t) => {
    if (tb.has(t)) intersection += 1;
  });
  return intersection / (ta.size + tb.size - intersection);
};

const REPEAT_THRESHOLD = 0.55;

/**
 * If the current question is a near-duplicate of an earlier one, return the
 * earlier answer text so the composer can offer a prefill chip. Returns null
 * when nothing similar was answered.
 */
export const findSimilarPriorAnswer = (
  currentQuestionText: string,
  history: TOnboardingSessionHistoryItem[]
): string | null => {
  let best: { score: number; text: string } | null = null;
  for (const entry of history) {
    const asked = entry.question?.question_text;
    const answered = entry.answer?.text;
    if (!asked || !answered || isSkipText(answered)) continue;
    const score = questionSimilarity(currentQuestionText, asked);
    if (score >= REPEAT_THRESHOLD && (!best || score > best.score)) {
      best = { score, text: answered };
    }
  }
  return best?.text ?? null;
};

// ---------------------------------------------------------------------------
// Progress projection — real numbers from the on-the-wire onboarding meta.
// ---------------------------------------------------------------------------

export type TSessionProgress = {
  areasCovered: number | null;
  areasTotal: number | null;
  percent: number | null;
  questionsAnswered: number | null;
  maxQuestions: number | null;
};

export const projectSessionProgress = (
  meta?: TOnboardingMetaPublic,
  progress?: TOnboardingProgress | null
): TSessionProgress | null => {
  if (!meta && !progress) return null;
  const percent =
    typeof meta?.overall_completeness === "number"
      ? Math.round(Math.min(100, Math.max(0, meta.overall_completeness * (meta.overall_completeness <= 1 ? 100 : 1))))
      : typeof progress?.completion_percentage === "number"
        ? Math.round(progress.completion_percentage)
        : null;
  return {
    areasCovered: meta?.handled_category_count ?? meta?.completed_category_count ?? null,
    areasTotal: null, // total categories isn't published; the bar uses percent instead
    percent,
    questionsAnswered: meta?.question_count ?? progress?.answered_questions ?? null,
    maxQuestions: meta?.max_questions ?? progress?.total_questions ?? null,
  };
};

// ---------------------------------------------------------------------------
// Summary stall detection — elapsed + stage-stagnation, NOT is_task_running
// (which is web-process-local and blind to queued TaskIQ work).
// ---------------------------------------------------------------------------

export const SUMMARY_STALL_SECONDS = 8 * 60;

export type TSummaryStallInput = {
  status?: string | null;
  elapsedSeconds?: number | null;
  currentStage?: string | null;
  /** Timestamp (ms) when currentStage last changed; null = unknown. */
  stageChangedAt?: number | null;
  now?: number;
};

export const isSummaryStalled = ({
  status,
  elapsedSeconds,
  currentStage,
  stageChangedAt,
  now = Date.now(),
}: TSummaryStallInput): boolean => {
  if (status !== "processing") return false;
  if (typeof elapsedSeconds === "number" && elapsedSeconds >= SUMMARY_STALL_SECONDS) return true;
  if (currentStage && typeof stageChangedAt === "number") {
    return now - stageChangedAt >= SUMMARY_STALL_SECONDS * 1000;
  }
  return false;
};

// ---------------------------------------------------------------------------
// Submission queue helpers — the wire is strictly serial, so the composer owns
// a small FIFO of sends; one item is in-flight at a time.
// ---------------------------------------------------------------------------

export type TQueuedSendState = "queued" | "attaching" | "sending" | "awaiting" | "acked" | "failed";

export type TQueuedSend = {
  id: string;
  questionId: string;
  text: string;
  fileNames: string[];
  state: TQueuedSendState;
  error?: string;
};

/**
 * Whether a queued item may be dispatched now. `filesReady` is true when every
 * attached upload has resolved (success or given up by the user).
 */
export const canDispatchQueuedSend = (item: TQueuedSend, filesReady: boolean, inFlight: boolean): boolean =>
  !inFlight && (item.state === "queued" || (item.state === "attaching" && filesReady));

/** Bounded append — over-capacity drops the oldest and reports it dropped one. */
export const pushQueuedSend = (
  queue: TQueuedSend[],
  item: TQueuedSend,
  cap = MAX_QUEUED_SENDS
): { queue: TQueuedSend[]; dropped: TQueuedSend | null } => {
  if (queue.length < cap) return { queue: [...queue, item], dropped: null };
  const [dropped, ...rest] = queue;
  return { queue: [...rest, item], dropped };
};

/** Bounded event log — ring buffer used for debug/replay aids. */
export const pushEvent = <T>(log: T[], event: T, cap = EVENT_LOG_MAX): T[] => {
  const next = log.length >= cap ? log.slice(log.length - cap + 1) : [...log];
  next.push(event);
  return next;
};

// ---------------------------------------------------------------------------
// Close-frame classification — close codes/reasons map to a session outcome,
// not a generic error.
// ---------------------------------------------------------------------------

export type TCloseOutcome = "completed" | "vetting_required" | "access_denied" | "auth_failed" | "error";

export const classifyClose = (code: number, reason?: string | null): TCloseOutcome => {
  const text = (reason ?? "").toLowerCase();
  if (code === 1008 && text.includes("authentication")) return "auth_failed";
  if (text.includes("vetting")) return "vetting_required";
  if (text.includes("access denied")) return "access_denied";
  if (text.includes("already completed") || text.includes("onboarding complete")) return "completed";
  if (code === 1008) return "access_denied";
  return "error";
};
